'use client'

import { Button } from '@vinted/web-ui'

import { useState } from 'react'

import { find } from 'lodash'

import { PerformanceChartButton, ProfileItemBumpButton } from '@marketplace-web/vas/vas-feature'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { Grid as ItemGrid } from '@marketplace-web/common-components/grid-ui'
import { TrackedSellerProductItem } from '@marketplace-web/seller-item-box/seller-item-box-feature'
import { TrackedProductItem } from '@marketplace-web/item-box/item-box-feature'
import { getProductItemStatus, ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { impressionEvent, clickEvent } from '@marketplace-web/profile/profile-data'
import {
  BundleItemFooter,
  BundlingPolicyDialog,
  useItemSelection,
  getIsBundlingAllowed,
} from '@marketplace-web/bundles/items-bundle-feature'
import {
  cannotBundleButtonViewEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'
import { ErrorBoundary } from '@marketplace-web/error-display/error-display-util'
import { ComponentError } from '@marketplace-web/error-display/error-display-feature'

import { ItemAlertStatus } from '@marketplace-web/item-page/item-status-data'

import { EDIT_ITEM_URL, ITEM_URL } from '../../../../constants/routes'
import { logClosetItemError } from '../../../../utils/log'

type Props = {
  items: Array<ProductItemModel>
  userId: number
  allowBump: boolean
  isViewingSelf: boolean
  selectedItems: Array<ProductItemModel>
  selectionLimit?: number
  bundleTrackingId: string | null
  updateSelectedItems: (items: Array<ProductItemModel>) => void
  onItemClick?: (item: ProductItemModel, position: number, isSelected: boolean) => void
  showBundles: boolean
  disabled: boolean
}

const ItemsContainer = ({
  items,
  userId,
  allowBump,
  onItemClick,
  isViewingSelf,
  selectedItems,
  selectionLimit,
  bundleTrackingId,
  updateSelectedItems,
  showBundles,
  disabled = false,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('item')
  const breakpoints = useBreakpoint()

  const profileReworkAbTest = useAbTest('profile_wardrobe_web_rework')
  const isProfileReworkTestOn = profileReworkAbTest?.variant === 'on' && !breakpoints.portables

  const bundlingSellerProfileAbTest = useAbTest('bundling_seller_profile_v2')
  const isBundlingSellerProfileAbTestOn =
    bundlingSellerProfileAbTest?.variant === 'a' || bundlingSellerProfileAbTest?.variant === 'b'
  const [bundlingPolicyDialogUi, setBundlingPolicyDialogUi] = useState<{
    isOpen: boolean
    selectedItemId?: number
  }>({ isOpen: false })

  const handleItemVisibility: React.ComponentProps<
    typeof ItemGrid<ProductItemModel>
  >['onItemVisible'] = ({ item: { data, id }, index }) => {
    const event = impressionEvent({
      id,
      position: index + 1,
      contentType: 'item',
      contentSource: 'user_items',
      itemOwnerId: data.user.id || userId,
    })

    track(event)

    if (isBundlingSellerProfileAbTestOn && showBundles) {
      const isItemBundlingAllowed = getIsBundlingAllowed({
        item: data,
        bundleItems: items,
        firstSelectedItemId: selectedItems[0]?.id,
      })
      if (!isItemBundlingAllowed) {
        const cannotBundleViewEvent = cannotBundleButtonViewEvent({
          listingId: id.toString(),
          sellerId: userId.toString(),
          reasonForNotBundling: ReasonForNotBundling.ShippingSize,
          screen: 'user_profile',
          bundleSessionId: bundleTrackingId,
        })
        track(cannotBundleViewEvent)
      }
    }
  }

  const handleErrorLogging = (err: Error | null) => logClosetItemError(err)

  const { selectItem, isLimitReached } = useItemSelection({
    selectedItems,
    updateSelectedItems,
    selectionLimit,
    onItemClick,
    items,
    sellerId: userId,
    bundleTrackingId,
    screen: 'user_profile',
  })

  const handleBundleItemClick = (item: ProductItemModel, index: number) => () => {
    const itemSelected = !!find(selectedItems, { id: item.id })

    track(
      clickEvent({
        screen: 'user_profile',
        target: itemSelected ? 'remove_item_from_bundle' : 'add_item_to_bundle',
        targetDetails: JSON.stringify({
          item_id: item.id.toString(),
          bundle_id: bundleTrackingId,
        }),
      }),
    )

    selectItem(item, index)()
  }

  const renderItemFooter = (item: ProductItemModel, index: number) => {
    const { id } = item

    if (isBundlingSellerProfileAbTestOn && showBundles)
      return (
        <BundleItemFooter
          item={item}
          itemIndex={index}
          items={items}
          onClick={handleBundleItemClick}
          sellerId={userId}
          selectedItems={selectedItems}
          isSelectedItemLimitReached={() => isLimitReached()}
          onCantBundleButtonClick={() =>
            setBundlingPolicyDialogUi({ isOpen: true, selectedItemId: item.id })
          }
          bundleTrackingId={bundleTrackingId}
          isProfilePage
          screen="user_profile"
          disabled={disabled}
        />
      )

    if (!isViewingSelf) return null

    const status = getProductItemStatus(item, isViewingSelf)

    switch (status) {
      case ItemAlertStatus.ReplicaProof:
      case ItemAlertStatus.UnderReview:
        return (
          <Button
            text={translate('wardrobe.actions.edit')}
            theme="primary"
            size="small"
            url={EDIT_ITEM_URL(id)}
          />
        )

      case 'bumped': {
        return <PerformanceChartButton itemId={id} />
      }

      case 'draft': {
        return (
          <Button
            text={translate('drafts.finish_editing')}
            theme="primary"
            size="small"
            url={EDIT_ITEM_URL(id)}
          />
        )
      }

      default: {
        if (!allowBump) return null

        return <ProfileItemBumpButton itemId={item.id} canBump={item.canPushUp} />
      }
    }
  }

  const renderItem: React.ComponentProps<typeof ItemGrid<ProductItemModel>>['renderItem'] = (
    { data },
    index,
  ) => (
    <ErrorBoundary FallbackComponent={ComponentError} preventLog onError={handleErrorLogging}>
      {isViewingSelf ? (
        <TrackedSellerProductItem
          item={data}
          renderFooter={renderItemFooter(data, index)}
          showStatus
          tracking={{
            id: data.id,
            contentType: 'item',
            contentSource: 'user_items',
            position: index + 1,
            screen: 'current_user_profile',
          }}
          url={data.isDraft ? EDIT_ITEM_URL(data.id) : ITEM_URL(data.id)}
          hideBusinessBadge
        />
      ) : (
        <TrackedProductItem
          item={data}
          renderFooter={renderItemFooter(data, index)}
          showStatus
          viewingSelf={isViewingSelf}
          tracking={{
            id: data.id,
            contentType: 'item',
            contentSource:
              isBundlingSellerProfileAbTestOn && selectedItems.length > 0 ? 'bundle' : 'user_items',
            position: index + 1,
            screen: 'user_profile',
          }}
          url={data.isDraft ? EDIT_ITEM_URL(data.id) : ITEM_URL(data.id)}
          isBumpTextHidden
          openItemInNewTab={isBundlingSellerProfileAbTestOn && selectedItems.length > 0}
        />
      )}
    </ErrorBoundary>
  )

  return (
    <>
      <ItemGrid
        items={items.map(item => ({ id: item.id, data: item }))}
        renderItem={renderItem}
        onItemVisible={handleItemVisibility}
        widthResolver={() => (isProfileReworkTestOn ? 'one-fourth' : 'one-fifth')}
      />
      {isBundlingSellerProfileAbTestOn && (
        <BundlingPolicyDialog
          show={bundlingPolicyDialogUi.isOpen}
          bundleTrackingId={bundleTrackingId}
          sellerId={userId}
          selectedItemId={bundlingPolicyDialogUi.selectedItemId}
          onClose={() => setBundlingPolicyDialogUi({ isOpen: false })}
        />
      )}
    </>
  )
}

export default ItemsContainer
